import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { fetchTrashedDealsPaginated } from "../requests/Uploaded_Deals/UploadedDealsService";

const useTrashedDeals = () => {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 15,
    hasNext: false,
    hasPrev: false,
  });
  const [filters, setFilters] = useState({
    search: "",
    dateFrom: "",
    dateTo: "",
    sortBy: "deleted_at",
    sortOrder: "desc",
  });

  const searchTimeoutRef = useRef(null);

  const debouncedSearch = useCallback((searchTerm) => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: searchTerm }));
      setPagination((prev) => ({ ...prev, currentPage: 1 }));
    }, 500);
  }, []);

  const computePaginationFromResponse = useCallback((p, limit) => {
    const totalItems = p?.total ?? p?.totalItems ?? 0;
    const totalPages = Math.max(
      1,
      p?.last_page ?? p?.totalPages ?? (Math.ceil(totalItems / limit) || 1)
    );
    const currentPage = Math.max(
      1,
      Math.min(p?.current_page ?? p?.currentPage ?? 1, totalPages)
    );
    return {
      currentPage,
      totalPages,
      totalItems,
      itemsPerPage: limit,
      hasNext: currentPage < totalPages,
      hasPrev: currentPage > 1,
    };
  }, []);

  const fetchDeals = useCallback(
    async (
      page = 1,
      limit = 15,
      search = "",
      dateFrom = "",
      dateTo = "",
      sortOrder = "desc",
      sortBy = "deleted_at"
    ) => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetchTrashedDealsPaginated(
          page,
          limit,
          search,
          dateFrom,
          dateTo,
          sortOrder,
          sortBy
        );

        if (!response.status) {
          setError(response.message || "Error fetching trashed deals");
          toast.error(response.message || "Error fetching trashed deals");
          setDeals([]);
          return;
        }

        const list = Array.isArray(response.data) ? response.data : [];
        const p = response.pagination || {};
        const computed = computePaginationFromResponse(p, limit);

        setDeals(list);
        setPagination({
          ...computed,
          totalPages: p?.last_page ?? computed.totalPages,
          hasNext: computed.currentPage < computed.totalPages,
          hasPrev: computed.currentPage > 1,
        });
      } catch (err) {
        setError("Network error occurred");
        toast.error("Network error occurred");
        console.error("Error fetching trashed deals:", err);
        setDeals([]);
      } finally {
        setLoading(false);
      }
    },
    [computePaginationFromResponse]
  );

  useEffect(() => {
    fetchDeals(
      pagination.currentPage,
      pagination.itemsPerPage,
      filters.search,
      filters.dateFrom,
      filters.dateTo,
      filters.sortOrder,
      filters.sortBy
    );
  }, [
    pagination.currentPage,
    pagination.itemsPerPage,
    filters.search,
    filters.dateFrom,
    filters.dateTo,
    filters.sortOrder,
    filters.sortBy,
    fetchDeals,
  ]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  const changePage = useCallback((newPage) => {
    setPagination((prev) => ({ ...prev, currentPage: newPage }));
  }, []);

  const changeItemsPerPage = useCallback((newLimit) => {
    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
      itemsPerPage: newLimit,
    }));
  }, []);

  const updateDateFilter = useCallback((dateFrom, dateTo) => {
    setFilters((prev) => ({ ...prev, dateFrom, dateTo }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  const updateSort = useCallback((sortBy, sortOrder) => {
    setFilters((prev) => ({
      ...prev,
      sortBy: sortBy || prev.sortBy,
      sortOrder,
    }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  const clearDateFilter = useCallback(() => {
    setFilters((prev) => ({ ...prev, dateFrom: "", dateTo: "" }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  const refreshData = useCallback(() => {
    fetchDeals(
      pagination.currentPage,
      pagination.itemsPerPage,
      filters.search,
      filters.dateFrom,
      filters.dateTo,
      filters.sortOrder,
      filters.sortBy
    );
  }, [
    fetchDeals,
    pagination.currentPage,
    pagination.itemsPerPage,
    filters.search,
    filters.dateFrom,
    filters.dateTo,
    filters.sortOrder,
    filters.sortBy,
  ]);

  return {
    deals,
    loading,
    error,
    pagination,
    filters,
    debouncedSearch,
    changePage,
    changeItemsPerPage,
    updateDateFilter,
    updateSort,
    clearDateFilter,
    refreshData,
  };
};

export default useTrashedDeals;
